import React from 'react';
import { StressTestResults, ElementStressResult } from '../types/stressTest';
import { useNetworkElementsStore } from '../store/networkElementsStore';
import { findElementByMonitoringId } from './grid/utils/gridHelpers';
import { elementDisplayName } from '../utils/elementDisplayName';
import { puToVolts, puDeviationPct, NOMINAL_VOLTAGE_LN_V } from '../utils/electricalConstants';
import {
  VOLTAGE_OPERATIONAL_MIN_PU, VOLTAGE_OPERATIONAL_MAX_PU,
  VOLTAGE_WARNING_LOW_PU, VOLTAGE_WARNING_HIGH_PU,
  VOLTAGE_OPERATIONAL_MIN_V, VOLTAGE_OPERATIONAL_MAX_V,
  VOLTAGE_WARNING_LOW_V, VOLTAGE_WARNING_HIGH_V,
} from '../utils/voltageConstants';
import { LOADING_COMPLIANCE_LIMIT_PCT, LOADING_WARNING_PCT } from '../utils/loadingConstants';
import { fmt, fmtOpt } from '../utils/numberFormat';

/** Resource type to German label */
const TR_TYPE_LABELS: Record<string, string> = {
  wallbox: 'Wallbox',
  heat_pump: 'Wärmepumpe',
  pv_anlage: 'PV-Anlage',
  battery_storage: 'Batteriespeicher',
  air_conditioning: 'Klimaanlage',
};

interface StressTestDetailsModalProps {
  results: StressTestResults;
  onClose: () => void;
  isControlActive?: boolean;
}

/** Severity of a voltage value (p.u.): outside the EN 50160 band → critical, inside the warning margin → warning */
const getVoltageSeverity = (v: number): 'ok' | 'warning' | 'critical' => {
  if (v < VOLTAGE_OPERATIONAL_MIN_PU || v > VOLTAGE_OPERATIONAL_MAX_PU) return 'critical';
  if (v < VOLTAGE_WARNING_LOW_PU || v > VOLTAGE_WARNING_HIGH_PU) return 'warning';
  return 'ok';
};

/** Get severity color for a voltage value (p.u. internally) */
const getVoltageColor = (v: number) => {
  const severity = getVoltageSeverity(v);
  if (severity === 'critical') return { text: 'text-red-500', bg: 'bg-red-500' };
  if (severity === 'warning') return { text: 'text-orange-400', bg: 'bg-orange-400' };
  return { text: 'text-green-400', bg: 'bg-green-400' };
};

/** Get the worst severity between min and max */
const getWorstSeverity = (minV: number, maxV: number): 'ok' | 'warning' | 'critical' => {
  const a = getVoltageSeverity(minV);
  const b = getVoltageSeverity(maxV);
  if (a === 'critical' || b === 'critical') return 'critical';
  if (a === 'warning' || b === 'warning') return 'warning';
  return 'ok';
};

/** Per-phase voltage card: shows min/max as a range on a scale bar */
const PhaseVoltageCard = ({
  label,
  minV,
  maxV,
  phaseColor,
}: {
  label: string;
  minV: number | undefined;
  maxV: number | undefined;
  phaseColor: string;
}) => {
  const min = minV ?? 1.0;
  const max = maxV ?? 1.0;

  // Scale in volts across the EN 50160 band (207–253 V) mapped to 0%-100%
  const toPercentV = (volts: number) => Math.max(0, Math.min(100, ((volts - VOLTAGE_OPERATIONAL_MIN_V) / (VOLTAGE_OPERATIONAL_MAX_V - VOLTAGE_OPERATIONAL_MIN_V)) * 100));
  const toPercent = (v: number) => toPercentV(puToVolts(v));
  const tickStep = (VOLTAGE_OPERATIONAL_MAX_V - VOLTAGE_OPERATIONAL_MIN_V) / 4;

  const minPos = toPercent(min);
  const maxPos = toPercent(max);
  const rangeWidth = Math.max(1.5, maxPos - minPos); // minimum visible width
  const centerPos = toPercentV(NOMINAL_VOLTAGE_LN_V);
  const severity = getWorstSeverity(min, max);

  const rangeBg = severity === 'critical' ? 'bg-red-500' :
    severity === 'warning' ? 'bg-orange-400' : 'bg-green-400';

  const borderColor = severity === 'critical' ? 'border-red-500/30' :
    severity === 'warning' ? 'border-orange-400/30' : 'border-green-400/30';

  return (
    <div className={`flex-1 rounded-lg bg-primary-700/50 border ${borderColor} p-3`}>
      {/* Phase label */}
      <div className={`text-sm font-bold mb-2 ${phaseColor}`}>{label}</div>

      {/* Range bar */}
      <div className="relative h-3 rounded-full bg-primary-600 mb-1.5">
        {/* normal zone: inside the warning margin of the band (faint background) */}
        <div className="absolute h-full bg-green-900/20 rounded-full"
          style={{ left: `${toPercentV(VOLTAGE_WARNING_LOW_V)}%`, width: `${toPercentV(VOLTAGE_WARNING_HIGH_V) - toPercentV(VOLTAGE_WARNING_LOW_V)}%` }} />
        {/* Un center tick */}
        <div className="absolute h-full w-px bg-primary-400/60" style={{ left: `${centerPos}%` }} />
        {/* Min-Max range band */}
        <div className={`absolute h-full rounded-full ${rangeBg} opacity-80`}
          style={{ left: `${minPos}%`, width: `${rangeWidth}%` }} />
      </div>

      {/* Scale labels */}
      <div className="flex justify-between text-[11px] text-primary-500 mb-2 px-0.5">
        <span>{fmt(VOLTAGE_OPERATIONAL_MIN_V, 0)} V</span>
        <span>{fmt(VOLTAGE_OPERATIONAL_MIN_V + tickStep, 0)} V</span>
        <span>{fmt(NOMINAL_VOLTAGE_LN_V, 0)} V</span>
        <span>{fmt(VOLTAGE_OPERATIONAL_MAX_V - tickStep, 0)} V</span>
        <span>{fmt(VOLTAGE_OPERATIONAL_MAX_V, 0)} V</span>
      </div>

      {/* Numeric values */}
      <div className="grid grid-cols-2 gap-x-2 text-xs">
        <div>
          <span className="text-primary-400">Min </span>
          <span className={`font-bold ${getVoltageColor(min).text}`}>
            {fmt(puToVolts(min), 0)} V
          </span>
        </div>
        <div>
          <span className="text-primary-400">Max </span>
          <span className={`font-bold ${getVoltageColor(max).text}`}>
            {fmt(puToVolts(max), 0)} V
          </span>
        </div>
        <div className="text-primary-500 text-[10px]">{fmt(puDeviationPct(min), 1)} %</div>
        <div className="text-primary-500 text-[10px]">{fmt(puDeviationPct(max), 1)} %</div>
      </div>
    </div>
  );
};

/** Get loading color for cable table */
const getLoadingColor = (percent: number) => {
  if (percent >= LOADING_COMPLIANCE_LIMIT_PCT) return 'text-red-500 dark:text-red-400';
  if (percent >= LOADING_WARNING_PCT) return 'text-orange-500 dark:text-orange-400';
  return 'text-gray-800 dark:text-primary-200';
};

export const StressTestDetailsModal = ({ results, onClose, isControlActive }: StressTestDetailsModalProps) => {
  const { summary } = results;
  const controlledHouses = summary.controlledHouses ?? [];

  const criticalCount = summary.voltageViolationsCritical ?? 0;
  const warningCount = summary.voltageViolationsWarning ?? 0;

  // Per-phase PV is always equally distributed
  const pvPerPhase = summary.totalPvGenerationKw / 3;

  // Top 10 cables by loading
  const topCables: ElementStressResult[] = results.results
    ? Object.values(results.results)
        .filter((r): r is ElementStressResult => r != null && r.type === 'line')
        .sort((a, b) => b.loadingPercent - a.loadingPercent)
        .slice(0, 10)
    : [];

  // Top 10 houses by lowest min voltage
  const getElement = useNetworkElementsStore((s) => s.getElement);
  const elements = useNetworkElementsStore((s) => s.elements);
  // Load flow results are keyed by segment id ("<kabel-uuid>_seg<N>"); the
  // operator knows the cable by its name, so resolve segment -> parent cable.
  const cableLabel = (segmentId: string): string => {
    const el = findElementByMonitoringId(segmentId, elements);
    if (!el) return segmentId;
    const seg = segmentId.match(/_seg(\d+)$/);
    const name = elementDisplayName(el, getElement);
    return seg ? `${name} · Seg. ${Number(seg[1]) + 1}` : name;
  };
  const topLowVoltageHouses = results.results
    ? Object.values(results.results)
        .filter((r): r is ElementStressResult => r != null && r.type === 'hausanschluss')
        .map((r) => {
          const voltages = [r.voltageL1Pu, r.voltageL2Pu, r.voltageL3Pu].filter((v): v is number => v != null);
          const minVPu = voltages.length > 0 ? Math.min(...voltages) : (r.voltagePu ?? 1.0);
          return { ...r, minVPu };
        })
        .sort((a, b) => a.minVPu - b.minVPu)
        .slice(0, 10)
    : [];

  return (
    <div className="fixed inset-0 z-2000 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white dark:bg-primary-800 rounded-xl shadow-2xl border border-gray-300 dark:border-primary-500 w-[560px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-primary-600">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">Stresstest-Ergebnisse Detail</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:text-primary-400 dark:hover:text-primary-100 dark:hover:bg-primary-700 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Spannungsband per Phase */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-gray-900 dark:text-white font-bold mb-3">
              Spannungsband (L-N, {NOMINAL_VOLTAGE_LN_V} V)
            </h3>
            <div className="flex gap-2">
              <PhaseVoltageCard
                label="L1"
                minV={summary.minVoltageL1Pu}
                maxV={summary.maxVoltageL1Pu}
                phaseColor="text-red-400"
              />
              <PhaseVoltageCard
                label="L2"
                minV={summary.minVoltageL2Pu}
                maxV={summary.maxVoltageL2Pu}
                phaseColor="text-yellow-400"
              />
              <PhaseVoltageCard
                label="L3"
                minV={summary.minVoltageL3Pu}
                maxV={summary.maxVoltageL3Pu}
                phaseColor="text-blue-400"
              />
            </div>

            {/* Violations summary */}
            <div className="mt-2 flex flex-wrap gap-3">
              {criticalCount > 0 && (
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-red-700 dark:text-red-400 font-medium">
                    {criticalCount} kritisch (&gt;&plusmn;10%)
                  </span>
                </div>
              )}
              {warningCount > 0 && (
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  <span className="text-orange-700 dark:text-orange-400 font-medium">
                    {warningCount} Warnung (&gt;&plusmn;5%)
                  </span>
                </div>
              )}
              {criticalCount === 0 && warningCount === 0 && (
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-green-700 dark:text-green-400 font-medium">
                    Alle Spannungen im Normbereich
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Leistungsbilanz */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-gray-900 dark:text-white font-bold mb-3">
              Leistungsbilanz
            </h3>

            {/* Totals row */}
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-2.5 text-center">
                <p className="text-[10px] text-yellow-600 dark:text-yellow-400 mb-0.5">PV-Erzeugung</p>
                <p className="text-lg font-bold text-yellow-700 dark:text-yellow-300">
                  {fmt(summary.totalPvGenerationKw, 0)} kW
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2.5 text-center">
                <p className="text-[10px] text-purple-600 dark:text-purple-400 mb-0.5">Gesamtlast</p>
                <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
                  {fmt((summary.totalBaseLoadKw ?? 0), 0)} kW
                </p>
              </div>
              <div className={`rounded-lg p-2.5 text-center ${
                (summary.totalNetPowerKw ?? 0) < 0
                  ? 'bg-amber-50 dark:bg-amber-900/20'
                  : 'bg-blue-50 dark:bg-blue-900/20'
              }`}>
                <p className="text-[10px] text-gray-600 dark:text-primary-400 mb-0.5">Netto</p>
                <p className={`text-lg font-bold ${
                  (summary.totalNetPowerKw ?? 0) < 0
                    ? 'text-amber-700 dark:text-amber-300'
                    : 'text-blue-700 dark:text-blue-300'
                }`}>
                  {fmt((summary.totalNetPowerKw ?? 0), 0)} kW
                </p>
              </div>
            </div>

            {/* Per-phase breakdown table */}
            {(summary.totalLoadL1Kw != null) && (
              <div className="rounded-lg border border-gray-200 dark:border-primary-600 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-primary-700/50">
                      <th className="px-3 py-1.5 text-left text-gray-500 dark:text-primary-400 font-medium">Phase</th>
                      <th className="px-3 py-1.5 text-right text-purple-600 dark:text-purple-400 font-medium">Last</th>
                      <th className="px-3 py-1.5 text-right text-yellow-600 dark:text-yellow-400 font-medium">PV</th>
                      <th className="px-3 py-1.5 text-right text-gray-600 dark:text-primary-300 font-medium">Netto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-primary-700">
                    {[
                      { label: 'L1', color: 'text-red-400', load: summary.totalLoadL1Kw ?? 0 },
                      { label: 'L2', color: 'text-yellow-400', load: summary.totalLoadL2Kw ?? 0 },
                      { label: 'L3', color: 'text-blue-400', load: summary.totalLoadL3Kw ?? 0 },
                    ].map(phase => (
                      <tr key={phase.label} className="hover:bg-gray-50/50 dark:hover:bg-primary-700/30">
                        <td className={`px-3 py-1.5 font-bold ${phase.color}`}>{phase.label}</td>
                        <td className="px-3 py-1.5 text-right text-gray-800 dark:text-primary-200 font-medium">
                          {fmt(phase.load, 1)} kW
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-800 dark:text-primary-200 font-medium">
                          {fmt(pvPerPhase, 1)} kW
                        </td>
                        <td className={`px-3 py-1.5 text-right font-bold ${
                          (phase.load - pvPerPhase) < 0
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-gray-800 dark:text-primary-200'
                        }`}>
                          {fmt((phase.load - pvPerPhase), 1)} kW
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Houses info */}
            <div className="mt-2 flex gap-3 text-xs text-gray-600 dark:text-primary-400">
              <span>Häuser mit PV: <span className="font-bold text-gray-800 dark:text-primary-200">{summary.housesWithPv ?? 0}</span></span>
              {(summary.housesExporting ?? 0) > 0 && (
                <span>davon einspeisend: <span className="font-bold text-amber-600 dark:text-amber-400">{summary.housesExporting}</span></span>
              )}
            </div>
          </div>

          {/* Netzauslastung */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-gray-900 dark:text-white font-bold mb-3">
              Netzauslastung
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-lg p-3 ${
                (summary.overloadedLines + summary.overloadedTransformers) > 0
                  ? 'bg-red-50 dark:bg-red-900/20'
                  : 'bg-green-50 dark:bg-green-900/20'
              }`}>
                <p className="text-xs text-gray-600 dark:text-primary-400 mb-1">Überlastungen</p>
                <p className={`text-xl font-bold ${
                  (summary.overloadedLines + summary.overloadedTransformers) > 0
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-green-700 dark:text-green-300'
                }`}>
                  {summary.overloadedLines + summary.overloadedTransformers}
                </p>
                <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-0.5">
                  {summary.overloadedLines} Leitungen, {summary.overloadedTransformers} Trafos
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Max. Auslastung</p>
                <p className={`text-xl font-bold ${
                  summary.maxLoadingPercent > LOADING_COMPLIANCE_LIMIT_PCT
                    ? 'text-red-700 dark:text-red-300'
                    : summary.maxLoadingPercent > LOADING_WARNING_PCT
                      ? 'text-orange-700 dark:text-orange-300'
                      : 'text-blue-700 dark:text-blue-300'
                }`}>
                  {fmt(summary.maxLoadingPercent, 1)}%
                </p>
              </div>
            </div>
          </div>

          {/* Geschaltete Elemente - only when control is active */}
          {isControlActive && summary.controlledHouseCount != null && summary.controlledHouseCount > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-wider text-gray-900 dark:text-white font-bold mb-3">
                Geschaltete Elemente
              </h3>

              {/* Summary bar */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-2 text-gray-600 dark:text-primary-400">
                <span>Häuser: <span className="font-bold text-gray-800 dark:text-primary-200">{summary.controlledHouseCount}</span></span>
                <span>Ressourcen: <span className="font-bold text-gray-800 dark:text-primary-200">{summary.controlledResourceCount ?? 0}</span></span>
                {summary.averageGzf != null ? (
                  <span>Ø GZF: <span className="font-bold text-blue-600 dark:text-blue-400">{fmt(summary.averageGzf, 2)}</span></span>
                ) : controlledHouses[0]?.resources[0] && (
                  <span>Limit: <span className="font-bold text-amber-600 dark:text-amber-400">{fmt(controlledHouses[0].resources[0].limitedToKw, 1)} kW</span></span>
                )}
                {(summary.controlSavedLoadKw ?? 0) > 0 && (
                  <span>Verbrauch limitiert: <span className="font-bold text-green-600 dark:text-green-400">{fmt(summary.controlSavedLoadKw!, 1)} kW</span></span>
                )}
                {(summary.controlSavedPvKw ?? 0) > 0 && (
                  <span>Einspeisung limitiert: <span className="font-bold text-yellow-600 dark:text-yellow-400">{fmt(summary.controlSavedPvKw!, 1)} kW</span></span>
                )}
              </div>

              {/* Detailed table */}
              {controlledHouses.length > 0 && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-700 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-amber-50 dark:bg-amber-900/30">
                        <th className="px-2 py-1.5 text-left text-amber-700 dark:text-amber-400 font-medium">Hausanschluss</th>
                        <th className="px-2 py-1.5 text-left text-amber-700 dark:text-amber-400 font-medium">Ressource</th>
                        <th className="px-2 py-1.5 text-right text-amber-700 dark:text-amber-400 font-medium">Original</th>
                        <th className="px-2 py-1.5 text-right text-amber-700 dark:text-amber-400 font-medium">Min.</th>
                        <th className="px-2 py-1.5 text-right text-amber-700 dark:text-amber-400 font-medium">Begrenzt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100 dark:divide-amber-800/30">
                      {controlledHouses.map((house) => (
                        <React.Fragment key={house.houseId}>
                          {/* Per-house subheader with GZF info */}
                          {house.pMin14aKw != null && (
                            <tr className="bg-amber-50/50 dark:bg-amber-900/10">
                              <td colSpan={5} className="px-2 py-1 text-[10px] text-gray-500 dark:text-primary-400">
                                <div className="flex items-center gap-1">
                                  <span className="text-sm">⌂</span>
                                  <span className="truncate max-w-[120px] font-medium text-gray-700 dark:text-primary-200" title={house.address}>{house.address}</span>
                                  <span className="mx-1">—</span>
                                  <span>{house.hasDigitalSteuerbox ? 'Digital' : 'Analog'}</span>
                                  <span className="mx-0.5">·</span>
                                  <span>{house.steuVeCount} SteuVE</span>
                                  <span className="mx-0.5">·</span>
                                  <span>GZF {fmtOpt(house.gzf, 2)}</span>
                                  <span className="mx-0.5">·</span>
                                  <span>P<sub>min</sub> {fmt(house.pMin14aKw, 1)} kW</span>
                                </div>
                              </td>
                            </tr>
                          )}
                          {house.resources.map((res) => (
                            <tr key={`${house.houseId}-${res.resourceId}`} className="hover:bg-amber-50/50 dark:hover:bg-amber-900/20">
                              <td className="px-2 py-1.5 text-gray-800 dark:text-primary-200">
                                {house.pMin14aKw == null && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-sm">⌂</span>
                                    <span className="truncate max-w-[120px]" title={house.address}>{house.address}</span>
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-gray-800 dark:text-primary-200">
                                {TR_TYPE_LABELS[res.resourceType] || res.resourceType}
                              </td>
                              <td className="px-2 py-1.5 text-right text-gray-500 dark:text-primary-400 line-through">
                                {fmt(res.originalMaxPowerKw, 1)} kW
                              </td>
                              <td className="px-2 py-1.5 text-right text-gray-500 dark:text-primary-400">
                                {res.minGuaranteedKw != null ? `${fmt(res.minGuaranteedKw, 1)} kW` : '—'}
                              </td>
                              <td className="px-2 py-1.5 text-right font-bold text-amber-600 dark:text-amber-400">
                                {fmt(res.limitedToKw, 1)} kW
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Kabel mit hoechster Auslastung */}
          {topCables.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-wider text-gray-900 dark:text-white font-bold mb-3">
                Kabel mit höchster Auslastung
              </h3>
              <div className="rounded-lg border border-gray-200 dark:border-primary-600 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-primary-700/50">
                      <th className="px-2 py-1.5 text-left text-gray-500 dark:text-primary-400 font-medium w-6">#</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 dark:text-primary-400 font-medium">Kabel</th>
                      <th className="px-2 py-1.5 text-right text-gray-500 dark:text-primary-400 font-medium">Auslastung</th>
                      <th className="px-2 py-1.5 text-right text-gray-500 dark:text-primary-400 font-medium" title="Strom des Kabels gegen die Sicherung am Abgang">Sicherung</th>
                      <th className="px-2 py-1.5 text-right text-gray-500 dark:text-primary-400 font-medium">Strom</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-primary-700">
                    {topCables.map((cable, index) => (
                      <tr key={cable.elementId} className="hover:bg-gray-50/50 dark:hover:bg-primary-700/30">
                        <td className="px-2 py-1.5 text-gray-400 dark:text-primary-500">{index + 1}</td>
                        <td className="px-2 py-1.5 text-gray-800 dark:text-primary-200 truncate max-w-[200px]" title={cable.elementId}>
                          {cableLabel(cable.elementId)}
                        </td>
                        <td className={`px-2 py-1.5 text-right font-bold ${getLoadingColor(cable.loadingPercent)}`}>
                          {fmt(cable.loadingPercent, 1)}%
                        </td>
                        <td className={`px-2 py-1.5 text-right ${cable.fuseLoadingPercent != null ? getLoadingColor(cable.fuseLoadingPercent) : 'text-gray-400 dark:text-primary-500'}`}>
                          {cable.fuseLoadingPercent != null ? `${fmt(cable.fuseLoadingPercent, 0)}% (${fmt(cable.fuseRatingA ?? 0, 0)} A)` : '–'}
                        </td>
                        <td className="px-2 py-1.5 text-right text-gray-800 dark:text-primary-200">
                          {fmt(cable.currentA, 1)} A
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Hausanschluesse mit niedrigster Spannung */}
          {topLowVoltageHouses.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-wider text-gray-900 dark:text-white font-bold mb-3">
                Hausanschlüsse mit niedrigster Spannung
              </h3>
              <div className="rounded-lg border border-gray-200 dark:border-primary-600 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-primary-700/50">
                      <th className="px-2 py-1.5 text-left text-gray-500 dark:text-primary-400 font-medium w-6">#</th>
                      <th className="px-2 py-1.5 text-left text-gray-500 dark:text-primary-400 font-medium">Hausanschluss</th>
                      <th className="px-2 py-1.5 text-right text-gray-500 dark:text-primary-400 font-medium">Min Spannung</th>
                      <th className="px-2 py-1.5 text-right text-gray-500 dark:text-primary-400 font-medium">Abweichung</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-primary-700">
                    {topLowVoltageHouses.map((house, index) => {
                      const el = getElement(house.elementId);
                      const street = el && 'street' in el ? (el as { street?: string | null }).street : null;
                      const houseNumber = el && 'houseNumber' in el ? (el as { houseNumber?: string | null }).houseNumber : null;
                      const address = street ? `${street} ${houseNumber || ''}`.trim() : house.elementId;
                      const voltageV = puToVolts(house.minVPu);
                      const deviation = puDeviationPct(house.minVPu);
                      const color = getVoltageColor(house.minVPu);
                      return (
                        <tr key={house.elementId} className="hover:bg-gray-50/50 dark:hover:bg-primary-700/30">
                          <td className="px-2 py-1.5 text-gray-400 dark:text-primary-500">{index + 1}</td>
                          <td className="px-2 py-1.5 text-gray-800 dark:text-primary-200 truncate max-w-[200px]" title={address}>
                            <div className="flex items-center gap-1">
                              <span className="text-sm">⌂</span>
                              <span className="truncate">{address}</span>
                            </div>
                          </td>
                          <td className={`px-2 py-1.5 text-right font-bold ${color.text}`}>
                            {fmt(voltageV, 1)} V
                          </td>
                          <td className={`px-2 py-1.5 text-right font-bold ${color.text}`}>
                            {fmt(deviation, 1)} %
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
